import { useTranslation } from 'react-i18next'

import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import Tooltip from '@/components/Tooltip/Tooltip'
import GradebookButton from '@/components/common/GradebookButton'
import TeachersColumn from '@/components/common/TeachersColumn/TeachersColumn'
import {
  useColumns,
  type RouteApiWithColumnsAndPagination
} from '@/hooks/useColumns'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { getColumnWidth } from '@/utils/get-column-width'

import ClassesColumn from '../components/ClassesColumn'
import styles from '../components/CoursesList.module.scss'
import type { Group } from '../types'

type UseCoursesColumnsProps = {
  semesterId: string | undefined
  routeApi: RouteApiWithColumnsAndPagination
}

const useCoursesColumns = ({
  semesterId,
  routeApi
}: UseCoursesColumnsProps) => {
  const { t } = useTranslation('courses')
  const { isExtraLarge } = useScreenResolution()

  return useColumns<Group>(
    [
      {
        key: 'course',
        dataIndex: ['course', 'displayName'],
        cellDataTestId: 'course-name',
        label: t('header.course'),
        width: TABLE_CELL_WIDTH.LG
      },
      {
        key: 'name',
        dataIndex: 'name',
        cellDataTestId: 'group-name',
        label: t('header.group'),
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'teachers',
        cellDataTestId: 'teacher-name',
        label: t('header.teachers'),
        render: (value: Group) => (
          <TeachersColumn teacher={value.teacher} coTeacher={value.coTeacher} />
        ),
        width: TABLE_CELL_WIDTH.XL
      },
      {
        key: 'students_count',
        cellDataTestId: 'students-number',
        label: t('header.students-number'),
        dataIndex: 'studentsCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG)
      },
      {
        key: 'student_classes',
        cellDataTestId: 'class-name',
        label: t('header.class'),
        render: (value: Group) => (
          <ClassesColumn studentClasses={value.studentClasses} />
        ),
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'lesson_scheduled_count',
        dataIndex: 'lessonCount',
        cellDataTestId: 'lessons-in-semester',
        title: (
          <Tooltip
            trigger={
              <span className={styles.column}>
                {t('header.lessons-scheduled')}
              </span>
            }
            text={t('help.lessons-scheduled-tooltip')}
          />
        ),
        label: t('header.lessons-scheduled'),
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'lesson_count',
        label: t('header.regular-lessons'),
        dataIndex: 'regularLessonCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG)
      },
      {
        key: 'lesson_covered_count',
        label: t('header.covered-lessons'),
        dataIndex: 'coveredLessonCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG)
      },
      {
        key: 'lesson_cancelled_count',
        label: t('header.cancelled-lessons'),
        dataIndex: 'cancelledLessonCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG)
      },
      {
        key: 'hours_per_day_count',
        label: t('header.hours-day'),
        dataIndex: 'hoursDayCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'hours_per_week_count',
        label: t('header.hours-week'),
        dataIndex: 'hoursWeekCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'hours_per_month_count',
        label: t('header.hours-month'),
        dataIndex: 'hoursMonthCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'hours_per_semester_count',
        label: t('header.hours-semester'),
        dataIndex: 'hoursSemesterCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'hours_per_year_count',
        label: t('header.hours-year'),
        dataIndex: 'hoursYearCount',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'gradebook',
        render: (value: Group) => (
          <GradebookButton
            courseId={value.course.id}
            groupId={value.id}
            semesterId={semesterId}
          />
        ),
        fixed: 'right',
        width: TABLE_CELL_WIDTH.ACTIONS,
        filled: true,
        alwaysVisible: true
      }
    ],
    { routeApi }
  )
}

export default useCoursesColumns
